// Shared 3-dot menu options + row-action dispatch + icon resolver used by
// both the list view (CustomFilelist) and the tile view (FileGridView).

import { useRef, useState } from 'react';
import { Checkbox, Input } from 'antd';
import {
  FaFile,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileCsv,
  FaFileImage,
  FaFileVideo,
  FaFileAudio,
  FaFileArchive,
  FaFileAlt,
} from 'react-icons/fa';
import type { IconType } from 'react-icons';
import type { FileActionDispatch, FileNode } from '../../../types/FileManagerTypes';
import { FileAction } from '../../../enums/fileActions';
import { AccessRequirements } from '../../../shared/utils/accessControl';
import { SPATIAL_PREVIEW_EXTS, fileActionAccess } from '../constants';
import { useFileListSelection } from '../../../hooks/useFileListSelection';
import ConditionalRender from '../../../shared/components/ConditionalRender';

export const NAME_REQUIRED_MESSAGE = 'Name is required';
export const DUPLICATE_NAME_MESSAGE = 'An item with this name already exists in this folder';

/** Basename + extension for rename. Folders and dotfiles keep the full name. */
export function splitEditableName(name: string, isDir?: boolean): { baseName: string; extension: string } {
  if (isDir) return { baseName: name, extension: '' };
  const lastDot = name.lastIndexOf('.');
  // No extension, trailing dot, or leading-dot-only names (.env, .gitignore).
  if (lastDot <= 0 || lastDot === name.length - 1) {
    return { baseName: name, extension: '' };
  }
  return { baseName: name.slice(0, lastDot), extension: name.slice(lastDot) };
}

export type RenameCommitResult = void | null | string | Promise<void | null | string>;

export interface FileOptionItem {
  id: string;
  label: string;
  danger?: boolean;
}

const DEFAULT_OPTIONS: FileOptionItem[] = [
  { id: FileAction.PREVIEW_FILE, label: 'Preview' },
  { id: FileAction.FILE_PERMISSIONS, label: 'Permissions' },
  { id: FileAction.RENAME_FILE, label: 'Rename' },
  { id: FileAction.MOVE_FILE, label: 'Move' },
  { id: FileAction.DOWNLOAD_FILES, label: 'Download' },
  { id: FileAction.REUPLOAD_FILE, label: 'Reupload' },
  { id: FileAction.DELETE_FILES, label: 'Delete' },
];

const MULTISELECT_OPTIONS: FileOptionItem[] = [
  { id: FileAction.MOVE_ALL, label: 'Move All' },
  { id: FileAction.DOWNLOAD_ALL, label: 'Download All' },
  { id: FileAction.DELETE_ALL, label: 'Delete All' },
];

const TRASH_OPTIONS: FileOptionItem[] = [
  { id: FileAction.RESTORE_FILE, label: 'Restore' },
  { id: FileAction.PERMANENTLY_DELETE_FILE, label: 'Permanently Delete' },
];

const TRASH_MULTISELECT_OPTIONS: FileOptionItem[] = [
  { id: FileAction.RESTORE_ALL, label: 'Restore All' },
  { id: FileAction.PERMANENTLY_DELETE_ALL, label: 'Permanently Delete All' },
];

// List-view "File type" column: the backend doesn't populate a type field, so
// derive it from the filename extension (e.g. "roads.shp" -> "SHP").
export function getFileTypeLabel(name: string): string {
  const lastDot = name.lastIndexOf('.');
  if (lastDot === -1 || lastDot === name.length - 1) return 'FILE';
  return name.slice(lastDot + 1).toUpperCase();
}

export function getFileOptions(isTrash: boolean, isMultiselect: boolean, canAccess: (requirements: AccessRequirements) => boolean): FileOptionItem[] {
  const options = isTrash ? (isMultiselect ? TRASH_MULTISELECT_OPTIONS : TRASH_OPTIONS) : isMultiselect ? MULTISELECT_OPTIONS : DEFAULT_OPTIONS;
  return options.filter((option) => canAccess(fileActionAccess(option.id)));
}

const FOLDER_DISALLOWED_OPTIONS = new Set<string>([FileAction.PREVIEW_FILE, FileAction.REUPLOAD_FILE]);

export function getRowOptionsForFile(file: FileNode, baseOptions: FileOptionItem[]): FileOptionItem[] {
  return file.isDir ? baseOptions.filter((o) => !FOLDER_DISALLOWED_OPTIONS.has(o.id)) : baseOptions;
}

export function FileOptionsPanel({
  options,
  isMultiselect,
  onSelect,
}: {
  options: FileOptionItem[];
  isMultiselect: boolean;
  onSelect: (actionId: string) => void;
}) {
  return (
    <div className="cfl-options-panel">
      <div className="cfl-options-header">{isMultiselect ? 'Multiselect options' : 'File options'}</div>
      <ul>
        {options.map((item) => (
          <li
            key={item.id}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item.id);
            }}
          >
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Any "_all" action id dispatches against the full current selection (passed
// in via selectedFiles) so future bulk handlers (delete_all, restore_all, ...)
// just work once wired in useFileActionHandler -- no changes needed here.
// "rename_file" is intercepted into inline edit rather than the prompt()-based handler.
export function dispatchRowAction(
  actionId: string,
  file: FileNode,
  handlers: {
    onFileAction: FileActionDispatch;
    onRequestRename?: (file: FileNode) => void;
    onClose?: () => void;
  },
  selectedFiles?: FileNode[],
) {
  if (actionId === FileAction.RENAME_FILE) {
    handlers.onRequestRename?.(file);
    handlers.onClose?.();
    return;
  }
  const files = actionId.endsWith('_all') && selectedFiles?.length ? selectedFiles : [file];
  handlers.onFileAction(actionId, files);
  handlers.onClose?.();
}

// TODO: Combine NewFolderInput and InlineRenameInput into a single unified
// component once the larger File Manager structure is finalized. These share
// ~70% of their logic and differ only in loading state, blur behavior, and
// prop signatures. See useInlineInputBehavior hook below for extracted common logic.

/**
 * Shared behavior for inline name input fields (create folder / rename file).
 * Handles Escape-key cancellation with blur suppression to prevent accidental commits.
 *
 * @param onCancel - Called when Escape is pressed
 * @param onBlur - Called when input loses focus (unless cancelled via Escape)
 * @returns Event handlers and ref for the Input component
 */
function useInlineInputBehavior({ onCancel, onBlur }: { onCancel: () => void; onBlur: (e: React.FocusEvent<HTMLInputElement>) => void }) {
  const cancelledRef = useRef(false);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      cancelledRef.current = true;
      onCancel();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    onBlur(e);
  };

  return {
    handleKeyDown,
    handleBlur,
  };
}

// Inline "insert name" input for the temp new-folder row/tile. Shared so both
// views stay in sync. The cancelledRef suppresses the blur that fires when Esc
// unmounts the input (which would otherwise re-commit the stale value).
export const NewFolderInput = ({
  submitting,
  defaultName,
  creatingClassName,
  onCommit,
  onCancel,
}: {
  submitting: boolean;
  defaultName: string;
  creatingClassName: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) => {
  const { handleKeyDown, handleBlur } = useInlineInputBehavior({
    onCancel,
    onBlur: (e) => {
      const value = e.target.value.trim();
      value ? onCommit(value) : onCancel();
    },
  });

  return (
    <ConditionalRender
      visible={submitting}
      fallback={
        <Input
          className="inline-name-input"
          size="small"
          autoFocus
          defaultValue={defaultName}
          onFocus={(e) => e.target.select()}
          onPressEnter={(e) => onCommit((e.target as HTMLInputElement).value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
        />
      }
    >
      <span className={creatingClassName}>Creating Folder…</span>
    </ConditionalRender>
  );
};

// Inline rename input shared by both views.
// Enter saves; Escape or blur (click outside) cancels. Files edit basename only
// (extension is re-attached on commit). Empty/duplicate show a single-line
// inline error (error slot is always reserved to avoid layout jump).
export const InlineRenameInput = ({
  file,
  onCommit,
  onCancel,
}: {
  file: FileNode;
  /** Return a string to keep edit mode open and show that inline error. */
  onCommit: (file: FileNode, value: string) => RenameCommitResult;
  onCancel: (file: FileNode) => void;
}) => {
  const { baseName, extension } = splitEditableName(file.name, file.isDir);
  const [value, setValue] = useState(baseName);
  const [error, setError] = useState<string | null>(null);
  // Suppress the blur that fires when Escape unmounts the input. Do not set
  // this on Enter: validation errors keep edit mode open, and the user must
  // still be able to cancel via click-outside afterward.
  const skipBlurRef = useRef(false);

  const handleCancel = () => {
    skipBlurRef.current = true;
    onCancel(file);
  };

  const handleCommit = async () => {
    const trimmed = value.trim();
    if (!trimmed) {
      setError(NAME_REQUIRED_MESSAGE);
      return;
    }
    const result = await onCommit(file, `${trimmed}${extension}`);
    if (typeof result === 'string' && result) {
      setError(result);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (error) setError(null);
  };

  const handlePressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    void handleCommit();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  return (
    <div className={`inline-name-field${error ? ' has-error' : ''}`}>
      <Input
        className="inline-name-input"
        size="small"
        autoFocus
        value={value}
        placeholder="Insert name"
        onClick={(e) => e.stopPropagation()}
        onFocus={(e) => e.target.select()}
        onChange={handleChange}
        onPressEnter={handlePressEnter}
        onKeyDown={handleInputKeyDown}
        onBlur={() => {
          if (skipBlurRef.current) {
            skipBlurRef.current = false;
            return;
          }
          onCancel(file);
        }}
      />
      {/* Always mounted so list/tile can reserve one line of space while editing. */}
      <div className="inline-name-error" aria-live="polite">
        {error ?? ''}
      </div>
    </div>
  );
};

// Select-all for every file on the page. Rendered by the list view's header
// row and by the toolbar in tile view; both derive from the same `selection`,
// so the two stay in sync.
export const SelectAllCheckbox = ({
  files,
  selection,
  setSelection,
  onFileAction,
}: {
  files: (FileNode | null | undefined)[];
  selection: string[];
  setSelection: (ids: string[]) => void;
  onFileAction: FileActionDispatch;
}) => {
  const { getHeaderCheckboxState, toggleSelectAll } = useFileListSelection({
    files,
    selection,
    setSelection,
    onFileAction,
  });
  const state = getHeaderCheckboxState();

  return (
    <Checkbox
      className="custom-checkbox"
      checked={state === 'checked'}
      indeterminate={state === 'indeterminate'}
      onChange={toggleSelectAll}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

export function resolvedTileIcon(file: FileNode): string {
  return file?.isDir ? '/assets/tile-view-folder.svg' : '/assets/file-icon-list.svg';
}

// Standard (non-spatial) file extensions → font-awesome icon. Groupings mirror
// fileTypeMap's categories; spatial files keep the existing svg (below).
const EXT_ICON: Record<string, IconType> = {
  pdf: FaFilePdf,
  doc: FaFileWord,
  docx: FaFileWord,
  odt: FaFileWord,
  xls: FaFileExcel,
  xlsx: FaFileExcel,
  ods: FaFileExcel,
  csv: FaFileCsv,
  jpg: FaFileImage,
  jpeg: FaFileImage,
  png: FaFileImage,
  gif: FaFileImage,
  bmp: FaFileImage,
  webp: FaFileImage,
  svg: FaFileImage,
  mp4: FaFileVideo,
  avi: FaFileVideo,
  mov: FaFileVideo,
  mkv: FaFileVideo,
  webm: FaFileVideo,
  mp3: FaFileAudio,
  wav: FaFileAudio,
  aac: FaFileAudio,
  ogg: FaFileAudio,
  flac: FaFileAudio,
  zip: FaFileArchive,
  rar: FaFileArchive,
  '7z': FaFileArchive,
  tar: FaFileArchive,
  gz: FaFileArchive,
  txt: FaFileAlt,
};

const extOf = (name?: string): string => name?.split('.').pop()?.toLowerCase() ?? '';

// Folder → per-view folder svg; spatial file → existing generic file svg;
// standard file → type-based font-awesome icon.
export const FileTypeIcon = ({ file, variant, className }: { file: FileNode; variant: 'list' | 'tile'; className?: string }) => {
  if (file?.isDir) {
    const src = variant === 'tile' ? '/assets/tile-view-folder.svg' : '/assets/folder-icon-list.svg';
    return <img className={className} src={src} alt="folder" />;
  }
  const ext = extOf(file?.name);
  if (SPATIAL_PREVIEW_EXTS.has(ext)) {
    return <img className={className} src="/assets/file-icon-list.svg" alt="file" />;
  }
  const Icon = EXT_ICON[ext] ?? FaFile;
  return <Icon className={className} aria-label="file" />;
};
